import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Table, Loader, Segment, Header } from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../api';

const PLATFORMS = ['nick', 'volto', 'volto_slate', 'volto_vlt', 'plone'];

const coverageColor = (pct) => {
  if (pct >= 100) return '#21ba45';
  if (pct >= 90) return '#a5d6a7';
  if (pct >= 70) return '#fff176';
  if (pct >= 50) return '#ffb74d';
  return '#ef5350';
};

const CoverageDashboard = () => {
  const [data, setData] = useState(null);
  const [verification, setVerification] = useState({});
  const [findings, setFindings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      fetchApi(`${getApiPath()}/@polyglot/coverage`),
      fetchApi(`${getApiPath()}/@polyglot/verification`).catch(() => ({ items: [] })),
      fetchApi(`${getApiPath()}/@polyglot/qc-findings?mode=summary`).catch(() => ({ summary: {} })),
    ])
      .then(([coverageJson, verificationJson, findingsJson]) => {
        setData(coverageJson.items);
        // Index verification by language: { sv: { verified: N, total: M } }
        const vByLang = {};
        (verificationJson.items || []).forEach((row) => {
          if (!vByLang[row.language]) vByLang[row.language] = { verified: 0, total: 0 };
          vByLang[row.language].verified += parseInt(row.verified || 0, 10);
          vByLang[row.language].total += parseInt(row.total_translated || 0, 10);
        });
        setVerification(vByLang);
        // Index findings counts by language from facets
        const fByLang = {};
        const facets = findingsJson.summary?.facets || findingsJson.facets || {};
        // The summary mode doesn't have per-language counts, so use the
        // languages facet from a separate call
        setFindings(fByLang);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });

    // Separate call for per-language finding counts (facets from item mode)
    fetchApi(`${getApiPath()}/@polyglot/qc-findings?page_size=1`)
      .then((json) => {
        const fByLang = {};
        (json.facets?.languages || []).forEach((f) => {
          fByLang[f.value] = f.count;
        });
        setFindings(fByLang);
      })
      .catch(() => {});
  }, []);

  if (loading) {
    return (
      <Container id="polyglot-dashboard">
        <Loader active inline="centered" content="Loading coverage data..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container id="polyglot-dashboard">
        <Segment color="red">Error: {error}</Segment>
      </Container>
    );
  }

  // Group by language
  const byLanguage = {};
  (data || []).forEach((row) => {
    if (!byLanguage[row.language]) byLanguage[row.language] = {};
    byLanguage[row.language][row.platform] = row;
  });

  const languages = Object.keys(byLanguage).sort();

  // Compute totals per platform
  const platformTotals = {};
  PLATFORMS.forEach((p) => {
    const rows = (data || []).filter((r) => r.platform === p);
    if (rows.length > 0) {
      const totalTranslated = rows.reduce(
        (sum, r) => sum + parseInt(r.translated || 0, 10),
        0,
      );
      const totalTranslatable = rows.reduce(
        (sum, r) => sum + parseInt(r.translatable || 0, 10),
        0,
      );
      platformTotals[p] = totalTranslatable > 0
        ? ((totalTranslated / totalTranslatable) * 100).toFixed(1)
        : '0.0';
    }
  });

  return (
    <Container id="polyglot-dashboard" style={{ paddingTop: '2em', paddingBottom: '2em' }}>
      <Helmet title="Polyglot Translation Dashboard" />
      <Link to="/polyglot">&larr; Back to Polyglot</Link>
      <div className="polyglot-page-header">
        <h1>Coverage Dashboard</h1>
        <p className="subtitle">
          {languages.length} languages, {(data || []).length} coverage entries across{' '}
          {PLATFORMS.length} platforms
        </p>
      </div>

      <Table celled compact selectable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Language</Table.HeaderCell>
            {PLATFORMS.map((p) => (
              <Table.HeaderCell key={p} textAlign="center">
                <Link to={`/polyglot/browse/${p}`}>{p}</Link>
                <br />
                <small>{platformTotals[p] || '-'}%</small>
              </Table.HeaderCell>
            ))}
            <Table.HeaderCell textAlign="center">QC</Table.HeaderCell>
            <Table.HeaderCell textAlign="center">Findings</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {languages.map((lang) => (
            <Table.Row key={lang}>
              <Table.Cell>
                <Link to={`/polyglot/languages/${lang}`}>
                  <strong>{lang}</strong>
                </Link>
              </Table.Cell>
              {PLATFORMS.map((p) => {
                const row = byLanguage[lang][p];
                if (!row) {
                  return (
                    <Table.Cell key={p} textAlign="center" style={{ color: '#999' }}>
                      -
                    </Table.Cell>
                  );
                }
                const pct = parseFloat(row.coverage_pct) || 0;
                return (
                  <Table.Cell
                    key={p}
                    textAlign="center"
                    style={{
                      backgroundColor: coverageColor(pct),
                      color: pct < 50 ? '#fff' : '#000',
                      fontWeight: pct >= 100 ? 'bold' : 'normal',
                    }}
                  >
                    <Link
                      to={`/polyglot/browse/${p}?language=${lang}`}
                      style={{ color: 'inherit' }}
                    >
                      {pct.toFixed(1)}%
                      <br />
                      <small>
                        {row.translated}/{row.translatable}
                      </small>
                    </Link>
                  </Table.Cell>
                );
              })}
              <Table.Cell textAlign="center">
                {verification[lang] ? (
                  <Link to={`/polyglot/verification/${lang}`}>
                    {verification[lang].verified}/{verification[lang].total}
                    {verification[lang].verified === verification[lang].total && verification[lang].total > 0
                      ? ' \u2713'
                      : ''}
                  </Link>
                ) : (
                  <span style={{ color: '#999' }}>-</span>
                )}
              </Table.Cell>
              <Table.Cell textAlign="center">
                {findings[lang] ? (
                  <Link to={`/polyglot/qc/findings?language=${lang}`}>
                    <span style={{ color: findings[lang] > 10 ? '#f2711c' : '#666' }}>
                      {findings[lang]}
                    </span>
                  </Link>
                ) : (
                  <span style={{ color: '#999' }}>-</span>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Container>
  );
};

export default CoverageDashboard;
